/**
 * Date / time formatting helpers used across the app.
 *
 * Single-purpose functions, deliberately kept small:
 *   • parseBackendDate     — robust parser (the only piece that knows about
 *                            backend timezone quirks)
 *   • formatRelativeTime   — "5 minutes ago" / "in 2 hours" (relative copy)
 *   • formatAbsolute       — "Apr 25, 2026, 10:26 PM" (human-readable
 *                            absolute datetime; ideal for tooltips)
 *   • formatDate           — "04/25/2026" (locale-aware calendar date)
 *   • formatCalendarDate   — "Jul 27, 2026" (prose-style calendar date, no
 *                            time -- use over formatDate for badges/copy)
 *   • formatDateRange      — "Jul 27 – Aug 2, 2026" (a start/end span,
 *                            written the way a person would say it)
 *   • formatExpiryRelative — "Expires in 2 hours" / "Expired 5 minutes ago"
 *
 * Pick the one that matches the *intent* of the UI; do not stack them.
 */

/**
 * Parse a date input that may come from the backend without an explicit
 * timezone (FastAPI often returns naive ISO strings like
 * `2026-04-25T15:00:00`). Treat those as UTC so downstream math is stable
 * regardless of the viewer's local offset. Strings that already carry a
 * `Z` or `+hh:mm` are respected; numbers / `Date` are passed through.
 * Returns `null` when the input cannot be parsed.
 */
export function parseBackendDate(input: string | number | Date): Date | null {
  if (input == null) return null;
  let date: Date;
  if (typeof input === 'string') {
    // A date-only string (`YYYY-MM-DD`) has no time or zone, so the UTC
    // coercion below would push it to UTC midnight and drop a calendar day
    // for negative-offset viewers. Pin it to local midnight instead. (Every
    // current caller passes a datetime with a `T`, so this branch is a guard
    // against future misuse rather than a change to existing behavior.)
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(input);
    if (dateOnly) {
      const [, y, m, d] = dateOnly;
      const local = new Date(Number(y), Number(m) - 1, Number(d));
      return Number.isNaN(local.getTime()) ? null : local;
    }
    const hasTimezoneInfo =
      input.endsWith('Z') ||
      input.includes('+') ||
      (input.includes('T') && input.split('T')[1]?.includes('-'));
    if (hasTimezoneInfo) {
      date = new Date(input);
    } else if (input.includes('T')) {
      date = new Date(input + 'Z');
    } else {
      date = new Date(input);
    }
    if (Number.isNaN(date.getTime())) {
      // Last-resort: re-parse without our UTC suffix.
      date = new Date(input);
    }
  } else {
    date = new Date(input);
  }
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Relative time copy, e.g. "just now", "5 minutes ago", "in 2 hours".
 * Returns an empty string for unparseable input so callers can fall back
 * cleanly without a try/catch.
 */
export function formatRelativeTime(
  input: string | number | Date,
  nowInput?: string | number | Date,
): string {
  const parsed = parseBackendDate(input);
  if (!parsed) return '';
  const now = nowInput ? parseBackendDate(nowInput)?.getTime() ?? Date.now() : Date.now();
  const time = parsed.getTime();

  const diffMs = time - now; // past => negative
  const abs = Math.abs(diffMs);

  const sec = 1000;
  const min = 60 * sec;
  const hr = 60 * min;
  const day = 24 * hr;
  const mon = 30 * day;
  const yr = 365 * day;

  if (abs < 45 * sec) return 'just now';
  if (abs < 90 * sec) return diffMs < 0 ? '1 minute ago' : 'in 1 minute';
  if (abs < 45 * min) {
    const v = Math.round(abs / min);
    return diffMs < 0 ? `${v} minutes ago` : `in ${v} minutes`;
  }
  if (abs < 90 * min) return diffMs < 0 ? '1 hour ago' : 'in 1 hour';
  if (abs < 22 * hr) {
    const v = Math.round(abs / hr);
    return diffMs < 0 ? `${v} hours ago` : `in ${v} hours`;
  }
  if (abs < 36 * hr) return diffMs < 0 ? '1 day ago' : 'in 1 day';
  if (abs < 25 * day) {
    const v = Math.round(abs / day);
    return diffMs < 0 ? `${v} days ago` : `in ${v} days`;
  }
  if (abs < 45 * day) return diffMs < 0 ? '1 month ago' : 'in 1 month';
  if (abs < 345 * day) {
    const v = Math.round(abs / mon);
    return diffMs < 0 ? `${v} months ago` : `in ${v} months`;
  }
  if (abs < 545 * day) return diffMs < 0 ? '1 year ago' : 'in 1 year';
  const v = Math.round(abs / yr);
  return diffMs < 0 ? `${v} years ago` : `in ${v} years`;
}

/**
 * Human-readable absolute datetime in the viewer's local timezone — e.g.
 * "Apr 25, 2026, 10:26 PM". Use this for tooltips that should add precise
 * timestamp information on top of a relative label, not for primary copy.
 */
export function formatAbsolute(
  input: string | number | Date,
  options?: { withSeconds?: boolean; locale?: string },
): string {
  const date = parseBackendDate(input);
  if (!date) return '';
  return new Intl.DateTimeFormat(options?.locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: options?.withSeconds ? '2-digit' : undefined,
  }).format(date);
}

/**
 * Locale-aware calendar date — "04/25/2026" (US) or "25/04/2026" (other
 * locales). Use when only the day matters and the time would be noise.
 */
export function formatDate(input: string | number | Date, locale: string = 'en-US'): string {
  const date = parseBackendDate(input);
  if (!date) return '';
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/**
 * Human-readable calendar date, no time — "Jul 27, 2026". Use wherever a
 * bare `YYYY-MM-DD` (or any date/datetime) would otherwise get shown
 * as-is; unlike `formatDate`'s slash format, this reads as prose rather
 * than a form field.
 */
export function formatCalendarDate(input: string | number | Date | null | undefined, locale?: string): string {
  if (input == null) return '';
  const date = parseBackendDate(input);
  if (!date) return '';
  return new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
}

/**
 * A date span written the way a person would say it -- "Jul 27 – Aug 2,
 * 2026" when both ends fall in the same year (the year is stated once,
 * at the end, instead of twice), or "Dec 29, 2025 – Jan 4, 2026" when the
 * span crosses a year boundary. Every "this week vs. the last 4 weeks"
 * style window across Contact Pulse's tabs/chips/tooltips should go
 * through this rather than interpolating raw `YYYY-MM-DD` strings.
 */
export function formatDateRange(
  start: string | number | Date | null | undefined,
  end: string | number | Date | null | undefined,
  locale?: string,
): string {
  const s = start != null ? parseBackendDate(start) : null;
  const e = end != null ? parseBackendDate(end) : null;
  if (!s && !e) return '';
  if (s && !e) return formatCalendarDate(s, locale);
  if (!s && e) return formatCalendarDate(e, locale);
  const sameYear = s!.getFullYear() === e!.getFullYear();
  const startStr = sameYear
    ? new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(s!)
    : formatCalendarDate(s!, locale);
  return `${startStr} \u2013 ${formatCalendarDate(e!, locale)}`;
}

/**
 * Caption helper for "expiry" copy that reads naturally regardless of
 * whether the timestamp is in the past or the future. Examples:
 *   future → "Expires in 2 hours"
 *   past   → "Expired 5 minutes ago"
 *   bad    → "" (caller can fall back)
 */
export function formatExpiryRelative(
  input: string | number | Date | null | undefined,
): string {
  if (input == null) return '';
  const parsed = parseBackendDate(input);
  if (!parsed) return '';
  const verb = parsed.getTime() < Date.now() ? 'Expired' : 'Expires';
  return `${verb} ${formatRelativeTime(parsed)}`;
}
